import { AfterViewInit, Component, ElementRef, EventEmitter, OnInit, HostListener, Output } from '@angular/core';

import { CartState } from '../../api/cartState';
import { untilComponentDestroyed, OnDestroyMixin } from '@w11k/ngx-componentdestroyed';

import { env } from '../../../environments/environment';
import { PaymentService } from '../../payment.service';
import { Web3Service } from '../../web3.service';
import { GlobalService } from '../../global.service';
import { CrisprCartService } from '../crispr-cart.service';
import { CrisprService } from '../crispr.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { defs } from '../../defs';
import { NGXLogger } from 'ngx-logger';
import { Money } from '../../bo/money';
import { CatPartBaseDto } from '../../api/catPartBaseDto';
import { DigitsInfo } from '../../bo/digitsInfo';
import { PayPalConfig } from '../../bo/payPalConfig';
import { BuyResponseDto, ExchangeRatesDto } from '../../api';
import { Currency } from '../../api/currency';

declare let paypal: any;

@Component({
	selector: 'narwhalia-crispr-cart',
	templateUrl: './crispr-cart.component.html',
	styleUrls: ['./crispr-cart.component.scss']
})
export class CrisprCartComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
	@Output() closePopup: EventEmitter<boolean>;
	cart: Map<CatPartBaseDto, number>;
	cartKeys: CatPartBaseDto[];
	cartPriceSum: number;
	digitsInfo: DigitsInfo;
	paypalConfig: PayPalConfig;
	cartQuantitySum: number;
	rates: ExchangeRatesDto;
	price: Money;
	Currency = Currency;
	glitterBalance: number;
	cartState: CartState;
	etherBalance: number;

	private paypalMethods = {
		payment: () =>
			this.crisprService.buyCrisprCart(Currency.USD).then(res => {
				this.cartState = 'payment_in_progress';
				return res.payPalPaymentId;
			}),
		onAuthorize: (data, actions) =>
			this.paymentService
				.finalizePayPalPayment(data.paymentID, data.payerID)
				.then(() => {
					this.crisprCartService.emptyCart();
					this.close();
				})
				.catch(() =>
					this.translateService
						.get('error.payment.pay_pal_rest_exception')
						.subscribe(translatedText => this.toastrService.error(translatedText))
				),
		onCancel: data =>
			this.paymentService
				.deletePayPalPayment(data.paymentID)
				.then(() => this.close())
				.catch(() =>
					this.translateService
						.get('error.payment.pay_pal_rest_exception')
						.subscribe(translatedText => this.toastrService.error(translatedText))
				)
	};

	@HostListener('document:click', ['$event'])
	clickedOutside(event) {
		if (!this.elementRef.nativeElement.contains(event.target)) {
			this.close();
		}
	}

	constructor(
		private elementRef: ElementRef,
		private crisprService: CrisprService,
		private crisprCartService: CrisprCartService,
		private paymentService: PaymentService,
		private globalService: GlobalService,
		private toastrService: ToastrService,
		private translateService: TranslateService,
		private web3Service: Web3Service,
		private log: NGXLogger
	) {
		super();
		this.digitsInfo = defs.digitsInfo;
		this.initCartSums();
		this.closePopup = new EventEmitter();
		this.paypalConfig = {
			env: env.payPal.env,
			commit: env.payPal.commit,
			style: env.payPal.style,
			payment: this.paypalMethods.payment,
			onAuthorize: this.paypalMethods.onAuthorize,
			onCancel: this.paypalMethods.onCancel
		};
	}

	close = () => {
		this.closePopup.emit();
	};

	initCartSums() {
		this.cartPriceSum = 0;
		this.cartQuantitySum = 0;
	}

	ngOnInit() {
		this.globalService.exchangeRates.pipe(untilComponentDestroyed(this))
			.subscribe((rates: ExchangeRatesDto) => {
				this.rates = rates;
				this.getCart();
			});
		this.getCartState();
		this.globalService.getBalances().pipe(untilComponentDestroyed(this))
			.subscribe(balances => {
				this.glitterBalance = balances.balance;
			});
		this.web3Service.getBalance()
			.then(etherBalance => {
				this.etherBalance = etherBalance;
			})
			//.catch(this.handleError);
	}

	getCart = () => {
		this.crisprCartService.cart$.pipe(untilComponentDestroyed(this))
			.subscribe(cart => {
				this.cartKeys = Array.from(cart.keys());
				this.cart = cart;
				this.initCartSums();
				for (const itemQuantity of cart) {
					const [item, quantity] = itemQuantity;
					this.cartQuantitySum += quantity;
					this.cartPriceSum += item.price * quantity;
				}
				this.price = {
					usd: this.cartPriceSum * this.rates.gli_usd,
					gli: this.cartPriceSum,
					eth: (this.cartPriceSum * this.rates.gli_usd) / this.rates.eth_usd
				};
			});
	};

	getCartState = () => {
		this.crisprCartService.cartState$.pipe(untilComponentDestroyed(this))
			.subscribe(cartState => (this.cartState = cartState));
		this.crisprService.getCrisprCart().pipe(untilComponentDestroyed(this))
			.subscribe(result => {
				this.crisprCartService.initCart(result);
			});
	};

	ngAfterViewInit() {
		paypal.Button.render(this.paypalConfig, '#paypal-button-crispr');
	}

	buyCart(currency: Currency) {
		this.web3Service
			.init()
			.then(() => this.crisprService.buyCrisprCart(currency))
			.then(this.transfer)
			.then(this.close)
			.catch(err => {
				this.log.error(err);
				this.close();
			})
	}

	transfer = (response: BuyResponseDto) => {
		this.cartState = CartState.PaymentStarted;
		return this.web3Service.send(response)
	}
}